import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Info, Warning } from '@phosphor-icons/react';

const Toast = ({ message, type = 'info', isVisible, onClose, duration = 5000 }) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  const config = {
    success: {
      icon: CheckCircle,
      bgGradient: 'from-green-500 via-green-600 to-green-700',
      borderColor: 'border-green-400/50',
      iconColor: 'text-green-100'
    },
    error: {
      icon: XCircle,
      bgGradient: 'from-red-500 via-red-600 to-red-700',
      borderColor: 'border-red-400/50',
      iconColor: 'text-red-100'
    },
    warning: {
      icon: Warning,
      bgGradient: 'from-yellow-500 via-yellow-600 to-yellow-700',
      borderColor: 'border-yellow-400/50',
      iconColor: 'text-yellow-100'
    },
    info: {
      icon: Info,
      bgGradient: 'from-blue-500 via-blue-600 to-blue-700',
      borderColor: 'border-blue-400/50',
      iconColor: 'text-blue-100'
    }
  };

  const { icon: Icon, bgGradient, borderColor, iconColor } = config[type] || config.info;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -50, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: -50, x: '-50%' }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed top-6 left-1/2 z-[9999] max-w-md w-full px-4"
          role="alert"
          aria-live="assertive"
        >
          <div className={`bg-gradient-to-r ${bgGradient} text-white rounded-xl shadow-2xl border-2 ${borderColor} p-4 flex items-start gap-3`}>
            <Icon size={28} weight="fill" className={`${iconColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm leading-relaxed font-medium whitespace-pre-line break-words">
                {message}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 text-white/80 hover:text-white transition-colors ml-2"
              aria-label="Close notification"
            >
              <XCircle size={20} weight="bold" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
